import classNames from 'classnames';
import { Link } from 'react-router';
import { CycleTimeInfo } from '../../../shared/types/operation.ts';
import { getConfirmedScraps, getPotentialScraps } from '../../../shared/utils/scrapUtils.ts';
import { Operation, OperationWithAlarm, OperationWithLabel } from '../../types/sharedTypeImpl.ts';
import { formatCycleTime, formatDatetime, formatKpiAllAndRecent } from '../../utils/fieldFormats.ts';
import { LinkButton } from '../linkButton.tsx';
import { getOperationColor } from './operationColors.ts';
import { OperationDetails } from './operationDetails.tsx';


type OperationExt = OperationWithAlarm & OperationWithLabel;

interface Props {
    style?: React.CSSProperties;
    narrow?: boolean;
    // Array of type: Operation[] | OperationWithAlarm[] | OperationWithLabel[]
    operations: OperationExt[];
    showBatch: boolean;
    operationTypeIndicators: boolean;
    highlightPotentialScraps: boolean;
    showScrapLabels: boolean;
    showScrapReasons: boolean;
    showOrderNumbers: boolean;
    enableScrapReview: boolean;
    enableScrapClose: boolean;
    showScrapCloseValues?: boolean;
    showEditColumn: boolean;
    hideEditIfReviewedOrClosed: boolean;
    enableRevert: boolean;
    onEditClick?: (operation: Operation) => void;
    onScrapReviewClick?: (operation: Operation) => void;
    onScrapCloseClick?: (operation: Operation) => void;
    onRevertClick?: (operation: Operation) => void;
}

export function OperationsTable({
    style,
    narrow,
    operations,
    showBatch,
    operationTypeIndicators,
    highlightPotentialScraps,
    showScrapLabels,
    showScrapReasons,
    showOrderNumbers,
    enableScrapReview,
    enableScrapClose,
    showScrapCloseValues,
    showEditColumn,
    hideEditIfReviewedOrClosed,
    enableRevert,
    onEditClick,
    onScrapReviewClick,
    onScrapCloseClick,
    onRevertClick,
}: Props) {

    function getCycleTimeIf(kpis: CycleTimeInfo | null) {
        return kpis && kpis.numParts >= 5 ? formatCycleTime(kpis.cycleTimeMedian) : null;
    }

    return (
        <table className='operations-table' style={style}>
            <thead>
                <tr>
                    {operationTypeIndicators && <th style={{ width: '20px', minWidth: '20px' }} />}
                    {showBatch && <th style={{ width: '130px' }}>Equipment</th>}
                    {showBatch && <th style={{ width: '175px' }}>Part number</th>}
                    <th style={{ width: '390px' }}>Operation performed</th>
                    <th style={{ width: narrow ? '200px' : '300px' }}>Comment</th>
                    {showScrapLabels && <th style={{ width: '55px' }}>Label</th>}
                    {showScrapReasons && <th style={{ width: '100px' }}>Scrap reason</th>}
                    {showOrderNumbers && <th style={{ width: '80px' }}>Order number</th>}
                    <th style={{ width: narrow ? '75px' : '80px' }}>Potential scrap</th>
                    <th style={{ width: narrow ? '85px' : '90px' }}>Confirmed scrap</th>
                    <th style={{ width: '60px' }}>Part count</th>
                    <th style={{ width: narrow ? '120px' : '130px' }} title='Median cycle time after setup&#10;Latest 8 hours shown in parentheses'>Cycle time</th>
                    <th style={{ width: narrow ? '130px' : '140px' }}>Date/time</th>
                    <th style={{ width: '70px' }}>Operator</th>
                    {showEditColumn && <th style={{ width: narrow ? '40px' : '50px' }} />}
                    {enableScrapReview && <th style={{ width: '60px' }} />}
                    {enableScrapClose && <th style={{ width: '50px' }} />}
                    {enableRevert && <th style={{ width: '60px' }} />}
                </tr>
            </thead>

            <tbody>
                {operations.map((operation) => {
                    const warnPotentialScrap = highlightPotentialScraps && getPotentialScraps(operation) > 0;
                    const warnUnreleasedLabel = operation.scrapLabel != null && operation.scrapCloseForm != null && showScrapLabels;
                    const rowClass = classNames({ 'highlight--issue': warnPotentialScrap || warnUnreleasedLabel });

                    const potentialScraps = operation.potentialScraps || '';// Do not show 0
                    const isPotentialScrapHandled = potentialScraps && !!operation.scrapHandledAt;
                    const potentialScrapClass = classNames({ 'handled-potential-scrap': isPotentialScrapHandled });

                    const confirmedScrapFinal = getConfirmedScraps(operation) || '';// Do not show 0

                    const cycleTimes = formatKpiAllAndRecent(
                        getCycleTimeIf(operation.cycleTimeOverall),
                        getCycleTimeIf(operation.cycleTimeRecent),
                    );

                    const isReviewOrClosed = operation.scrapReview || operation.scrapCloseForm;
                    const hideEditButton = operation.operationType === 'Measurement'
                        || hideEditIfReviewedOrClosed && isReviewOrClosed;

                    return (
                        <tr key={operation._id} className={rowClass}>
                            {operationTypeIndicators &&
                                <td style={{ backgroundColor: getOperationColor(operation.operationType) }} />
                            }
                            {showBatch && <td className='equipment'>
                                <Link
                                    className='link-button--non-intrusive'
                                    to={`/machine-log#equipment=${operation.equipment}`}>
                                    {operation.equipmentDisplayName}
                                </Link>
                            </td>}
                            {showBatch && <td title={operation.product} className='product'>
                                <span style={{ wordBreak: 'break-word' }}>
                                    {operation.partDisplayName}
                                </span>
                            </td>}
                            <td className='details'><OperationDetails operation={operation} /></td>
                            <td className='comment'>
                                <div>{operation.comment}</div>
                                {(operation.scrapReview?.comment && showScrapCloseValues) &&
                                    <div style={{ borderTop: '1px solid #bbb' }}>{operation.scrapReview?.comment}</div>
                                }
                                {(operation.scrapCloseForm?.comment && showScrapCloseValues) &&
                                    <div style={{ borderTop: '1px solid #bbb' }}>{operation.scrapCloseForm?.comment}</div>
                                }
                            </td>
                            {showScrapLabels && <td>{operation.scrapLabel}</td>}
                            {showScrapReasons && <td>{operation.scrapCloseForm?.scrapReason || operation.scrapReview?.scrapReason || 'n/a'}</td>}
                            {showOrderNumbers && <td>{operation.orderNumber}</td>}
                            <td className={potentialScrapClass}>{potentialScraps}</td>
                            <td>{confirmedScrapFinal}</td>
                            <td>{operation.partCount}</td>
                            <td>{cycleTimes}</td>
                            <td>
                                <div>{formatDatetime(operation.effectiveTime)}</div>
                                {(operation.scrapReview?.time && showScrapCloseValues) &&
                                    <div style={{ borderTop: '1px solid #bbb' }}>{formatDatetime(operation.scrapReview?.time)}</div>
                                }
                                {(operation.scrapCloseForm?.time && showScrapCloseValues) &&
                                    <div style={{ borderTop: '1px solid #bbb' }}>{formatDatetime(operation.scrapCloseForm?.time)}</div>
                                }
                            </td>
                            <td>
                                <div>{operation.operator?.toUpperCase()}</div>
                                {(operation.scrapReview?.operator && showScrapCloseValues) &&
                                    <div style={{ borderTop: '1px solid #bbb' }}>{operation.scrapReview?.operator?.toUpperCase()}</div>
                                }
                                {(operation.scrapCloseForm?.operator && showScrapCloseValues) &&
                                    <div style={{ borderTop: '1px solid #bbb' }}>{operation.scrapCloseForm?.operator?.toUpperCase()}</div>
                                }
                            </td>
                            {showEditColumn && <td>
                                {!hideEditButton && <LinkButton onClick={() => onEditClick?.(operation)}>Edit</LinkButton>}
                            </td>}
                            {enableScrapReview && <td><LinkButton onClick={() => onScrapReviewClick?.(operation)}>Review</LinkButton></td>}
                            {enableScrapClose && <td><LinkButton onClick={() => onScrapCloseClick?.(operation)}>Close</LinkButton></td>}
                            {enableRevert && <td><LinkButton onClick={() => onRevertClick?.(operation)}>Revert</LinkButton></td>}
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}
